/**
 * Task-Specific Familiarisation - WBC3 Appendix 8 Section 7.1 Compliance
 * 
 * "All personnel shall receive training appropriate to the tasks they undertake"
 * 
 * Equipment and operation-specific familiarisation based on vessel configuration
 * This is where vessel-specific equipment training goes (cranes, winches, etc.)
 */

// Helper for vessel type normalization
const normalizeVesselType = (type) => {
  return (type || '').toLowerCase().trim();
};

export const TASK_SPECIFIC_FAMILIARISATION_ITEMS = [
  // CRANE OPERATIONS
  {
    id: 'crane_operation_safety',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Crane Operation and Safety Systems',
    description: 'Safe operation of vessel crane including load charts and safety systems',
    trigger: (vessel) => vessel.equipment && vessel.equipment.includes('winch_lifting_gear'),
    reference: 'Task-Appropriate Training',
    tag: 'Equipment-Specific',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Critical safety training for crane operations including load limits and exclusion zones. CUSTOMIZE: Add your vessel\'s specific crane model, SWL limits, and operational areas. (WBC3 Section 28.1.6)'
  },
  
  // TOWING OPERATIONS  
  {
    id: 'towing_winch_operation',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Towing Winch Operation and Emergency Release',
    description: 'Safe operation of towing winch and emergency release procedures',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'tug' || (vessel.equipment && vessel.equipment.includes('towing_winch')),
    reference: 'Task-Appropriate Training',
    tag: 'Vessel-Specific',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Essential towing safety including winch operation and emergency release systems. CUSTOMIZE: Add your vessel\'s specific winch controls, max tonnage, and emergency release procedures. (WBC3 Section 28.1.6)'
  },
  
  {
    id: 'towing_line_handling',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Towing Line Handling and Safety',
    description: 'Safe handling of towing lines including snap-back zones',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'tug',
    reference: 'Task-Appropriate Training',
    tag: 'Vessel-Specific', 
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Critical deck safety for towing operations including snap-back awareness. CUSTOMIZE: Define your vessel\'s specific snap-back zones and towing line handling procedures. (WBC3 Section 28.1.6)'
  },

  {
    id: 'heavy_duty_deck_machinery',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Heavy-Duty Deck Machinery (Towing Bollards, Fairleads, Capstans)',
    description: 'Safe operation of heavy-duty towing deck machinery and understanding of Safe Working Loads',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'tug',
    reference: 'Task-Appropriate Training',
    tag: 'Vessel-Specific',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Heavy-duty towing deck machinery safety including high-load bollard SWL and towing fairlead operation. CUSTOMIZE: Document your vessel\'s bollard SWL ratings and specific towing gear setup. (WBC3 Section 28.1.6)'
  },

  {
    id: 'ship_assist_operations',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Ship Assist Operations (Berthing/Unberthing)',
    description: 'Safe ship assist procedures including positioning and communication',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'tug',
    reference: 'Task-Appropriate Training',
    tag: 'Vessel-Specific',
    priority: 'high',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Ship assist operations including safe positioning and communication protocols. CUSTOMIZE: Add your port/harbor specific procedures and ship types typically assisted. (WBC3 Section 28.1.6)'
  },

  {
    id: 'towing_configurations',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Towing Configurations (Alongside, Stern, Emergency)',
    description: 'Different towing methods and their safety considerations',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'tug',
    reference: 'Task-Appropriate Training',
    tag: 'Vessel-Specific',
    priority: 'high',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Various towing configurations and their specific safety procedures. CUSTOMIZE: Document configurations your vessel uses and specific safety procedures for each. (WBC3 Section 28.1.6)'
  },

  {
    id: 'vessel_stability_towing',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Vessel Stability During Towing Operations',
    description: 'Understanding bollard pull limits and stability considerations',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'tug',
    reference: 'Task-Appropriate Training',
    tag: 'Vessel-Specific',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Critical understanding of vessel stability limits during towing operations. CUSTOMIZE: Reference your vessel\'s stability booklet and specific bollard pull limits. (WBC3 Section 28.1.6)'
  },

  {
    id: 'fender_systems_operation',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Fender Systems Operation and Deployment',
    description: 'Safe deployment and use of fender systems for ship assist',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'tug' && vessel.equipment && vessel.equipment.includes('fender_systems'),
    reference: 'Task-Appropriate Training',
    tag: 'Equipment-Specific',
    priority: 'medium',
    verifiable: true,
    canBeNA: true,
    wbc3Required: true,
    naReasons: ['No fender systems fitted', 'Fixed fendering only'],
    tooltip: 'Safe operation of deployable fender systems for ship assist operations. CUSTOMIZE: Add your vessel\'s specific fender type and deployment procedures. (WBC3 Section 28.1.6)'
  },

  {
    id: 'towing_communication_protocols',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Towing Communication Protocols',
    description: 'Communication procedures during towing and ship assist operations',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'tug',
    reference: 'Task-Appropriate Training',
    tag: 'Vessel-Specific',
    priority: 'high',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Essential communication protocols for safe towing operations. CUSTOMIZE: Add your port/harbor VHF channels and specific communication protocols. (WBC3 Section 28.1.6)'
  },

  // PASSENGER OPERATIONS
  {
    id: 'passenger_safety_briefing',
    classification: 'VESSEL_SPECIFIC',
    role: 'UNIVERSAL',
    text: 'Passenger Safety Briefing Procedures and Techniques',
    description: 'Procedures and techniques for conducting effective passenger safety briefings',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'pax' || vessel.passengerCapacity > 0,
    reference: 'Task-Appropriate Training / STCW V/2',
    tag: 'Vessel-Specific',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Critical passenger safety briefing including lifejacket demonstration and muster procedures. CUSTOMIZE: Add your vessel\'s specific briefing script and demonstration requirements. (WBC3 Section 28.1.6)'
  },

  {
    id: 'passenger_counting_procedures',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Passenger Counting and Embarkation Control',
    description: 'Accurate passenger counting procedures and embarkation/disembarkation control',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'pax' || vessel.passengerCapacity > 0,
    reference: 'Task-Appropriate Training',
    tag: 'Vessel-Specific',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Essential passenger counting and embarkation control for vessel stability and emergency response. CUSTOMIZE: Add your vessel\'s counting method and manifest requirements. (WBC3 Section 28.1.6)'
  },

  {
    id: 'pa_system_operation',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Public Address System Operation and Emergency Announcements',
    description: 'PA system operation and standardized emergency announcement procedures',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'pax' || vessel.passengerCapacity > 0,
    reference: 'Task-Appropriate Training',
    tag: 'Equipment-Specific',
    priority: 'critical',
    verifiable: true,
    canBeNA: true,
    wbc3Required: true,
    naReasons: ['No PA system fitted', 'Megaphone/loudhailer only'],
    tooltip: 'PA system operation for passenger communications and emergency announcements. CUSTOMIZE: Add your vessel\'s PA system operation and standard announcements. (WBC3 Section 28.1.6)'
  },

  {
    id: 'passenger_gangway_operation',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Passenger Gangway Operation and Safety',
    description: 'Safe deployment and operation of passenger gangways and boarding equipment',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'pax' || vessel.passengerCapacity > 0,
    reference: 'Task-Appropriate Training',
    tag: 'Equipment-Specific',
    priority: 'high',
    verifiable: true,
    canBeNA: true,
    wbc3Required: true,
    naReasons: ['Fixed gangway only', 'No dedicated passenger gangway'],
    tooltip: 'Safe operation of passenger gangways including deployment and safety protocols. CUSTOMIZE: Add your vessel\'s gangway type and deployment procedures. (WBC3 Section 28.1.6)'
  },

  {
    id: 'passenger_muster_procedures',
    classification: 'VESSEL_SPECIFIC',
    role: 'UNIVERSAL',
    text: 'Passenger Muster and Assembly Procedures',
    description: 'Passenger muster point management and emergency assembly procedures',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'pax' || vessel.passengerCapacity > 0,
    reference: 'Task-Appropriate Training / STCW V/2',
    tag: 'Vessel-Specific',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Critical passenger muster procedures and emergency assembly management. CUSTOMIZE: Add your vessel\'s muster station locations and assembly procedures. (WBC3 Section 28.1.6)'
  },

  {
    id: 'crowd_management_techniques',
    classification: 'VESSEL_SPECIFIC',
    role: 'UNIVERSAL',
    text: 'Crowd Management and Passenger Control',
    description: 'Crowd control techniques for normal operations and emergency situations',
    trigger: (vessel) => vessel.type === 'pax' || vessel.passengerCapacity > 6,
    reference: 'Task-Appropriate Training / STCW V/2',
    tag: 'Vessel-Specific',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Essential crowd management skills for passenger control in normal and emergency situations. CUSTOMIZE: Add your vessel\'s passenger capacity and crowd control procedures. (WBC3 Section 28.1.6)'
  },

  {
    id: 'passenger_emergency_evacuation',
    classification: 'VESSEL_SPECIFIC',
    role: 'UNIVERSAL',
    text: 'Passenger Emergency Evacuation Procedures',
    description: 'Evacuation procedures including passenger assistance and lifecraft loading',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'pax' || vessel.passengerCapacity > 0,
    reference: 'Task-Appropriate Training / STCW V/2',
    tag: 'Vessel-Specific',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Critical passenger evacuation procedures including assistance for elderly and disabled passengers. CUSTOMIZE: Add your vessel\'s evacuation routes and lifecraft loading procedures. (WBC3 Section 28.1.6)'
  },

  {
    id: 'passenger_vessel_stability',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Passenger Vessel Stability and Loading',
    description: 'Understanding passenger loading effects on vessel stability and trim',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'pax' || vessel.passengerCapacity > 0,
    reference: 'Task-Appropriate Training',
    tag: 'Vessel-Specific',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Critical understanding of how passenger loading affects vessel stability and safe operations. CUSTOMIZE: Add your vessel\'s passenger distribution limits and stability guidance. (WBC3 Section 28.1.6)'
  },

  // DYNAMIC POSITIONING
  {
    id: 'dp_system_operation',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Dynamic Positioning System Operation',
    description: 'Basic DP system operation and emergency procedures',
    trigger: (vessel) => vessel.equipment && vessel.equipment.includes('dynamic_positioning'),
    reference: 'Task-Appropriate Training',
    tag: 'Equipment-Specific',
    priority: 'high',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'DP system basics and emergency procedures for crew operating in DP mode. CUSTOMIZE: Add your vessel\'s specific DP system make/model and operational procedures. (WBC3 Section 28.1.6)'
  },

  // CREW TRANSFER OPERATIONS
  {
    id: 'personnel_transfer_safety',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Personnel Transfer Safety Procedures',
    description: 'Safe transfer operations including gangway and platform systems',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'ctv' || (vessel.operations && vessel.operations.includes('personnel_transfer')),
    reference: 'Task-Appropriate Training',
    tag: 'Vessel-Specific',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Critical safety for personnel transfer operations including equipment operation. CUSTOMIZE: Add your vessel\'s specific transfer method (step/gangway) and operational limits. (WBC3 Section 28.1.6)'
  },

  {
    id: 'motion_compensated_gangway',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Motion Compensated Gangway Operation',
    description: 'Safe operation of motion compensated gangway systems including deployment, positioning, and emergency retrieval',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'ctv' && vessel.equipment && vessel.equipment.includes('motion_compensated_gangway'),
    reference: 'Task-Appropriate Training / Industry Best Practice',
    tag: 'Equipment-Specific',
    priority: 'critical',
    verifiable: true,
    canBeNA: true,
    wbc3Required: true,
    naReasons: ['No motion compensated gangway fitted', 'Manual gangway only', 'Thrust-powered fender grip method only'],
    tooltip: 'Specialized training for motion compensated gangway operation including simulator training and emergency procedures. CUSTOMIZE: Add your gangway make/model, operational limits, and specific manufacturer procedures. (WBC3 Section 28.1.6)'
  },

  {
    id: 'ctv_close_maneuvering',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'CTV Close Maneuvering to Offshore Structures',
    description: 'Safe close approach procedures to wind turbines, platforms, and vessels including collision avoidance',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'ctv',
    reference: 'Task-Appropriate Training / Industry Safety Guidelines',
    tag: 'Vessel-Specific',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Critical close maneuvering skills for safe approach to offshore structures and collision risk management. CUSTOMIZE: Add wind farm/platform specific approach procedures from your operational area. (WBC3 Section 28.1.6)'
  },

  {
    id: 'ctv_dp_integration',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'CTV Dynamic Positioning Integration',
    description: 'Understanding DP system integration with transfer operations and gangway systems',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'ctv' && vessel.equipment && vessel.equipment.includes('dynamic_positioning'),
    reference: 'Task-Appropriate Training',
    tag: 'Equipment-Specific',
    priority: 'high',
    verifiable: true,
    canBeNA: true,
    wbc3Required: true,
    naReasons: ['No DP system fitted', 'Manual positioning only'],
    tooltip: 'DP system operation specific to CTV transfer operations including integration with gangway systems. CUSTOMIZE: Add your vessel\'s DP-gangway integration procedures and weather limits. (WBC3 Section 28.1.6)'
  },

  {
    id: 'ctv_fendering_systems',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'CTV Fendering Systems for Structure Contact',
    description: 'Safe deployment and management of active fender systems for turbine and platform contact',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'ctv',
    reference: 'Task-Appropriate Training / Industry Best Practice',
    tag: 'Vessel-Specific',
    priority: 'high',
    verifiable: true,
    canBeNA: true,
    wbc3Required: false,
    naReasons: ['Passive fendering only', 'No structure contact operations'],
    tooltip: 'Active fender system operation for safe contact with offshore structures during personnel transfer. CUSTOMIZE: Add your vessel\'s fender system type and structural contact procedures. (WBC3 Section 28.1.6)'
  },

  {
    id: 'ctv_weather_limitations',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'CTV Weather Limitations and Transfer Criteria',
    description: 'Understanding weather limits, sea state criteria, and transfer decision-making protocols',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'ctv',
    reference: 'Task-Appropriate Training / Vessel Operating Manual',
    tag: 'Vessel-Specific',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Critical understanding of weather limitations and safe transfer criteria including immediate stop procedures. CUSTOMIZE: Add your vessel\'s specific Hs limits and client-specific transfer criteria. (WBC3 Section 28.1.6)'
  },

  {
    id: 'ctv_fall_arrest_systems',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Personnel Fall Arrest and Safety Systems',
    description: 'Safe use of personnel safety harnesses, fall arrest systems, and emergency unclipping procedures',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'ctv',
    reference: 'Task-Appropriate Training / Personnel Safety Requirements',
    tag: 'Vessel-Specific',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Essential personnel safety systems training including emergency unclipping and fall arrest procedures. CUSTOMIZE: Add your vessel\'s specific fall arrest equipment and harness systems. (WBC3 Section 28.1.6)'
  },

  // SUPPORT VESSEL OPERATIONS
  {
    id: 'supply_transfer_operations',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Supply Transfer and Equipment Handling',
    description: 'Safe transfer of supplies, equipment, fuel, and water to offshore installations',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'support',
    reference: 'Task-Appropriate Training',
    tag: 'Vessel-Specific',
    priority: 'high',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Essential supply transfer procedures for offshore support operations. CUSTOMIZE: Add your vessel\'s specific supply transfer equipment and procedures. (WBC3 Section 28.1.6)'
  },

  {
    id: 'deck_cargo_securing',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Deck Cargo Securing for Sea',
    description: 'Heavy weather securing of deck cargo and equipment including lashing calculations',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'support',
    reference: 'Task-Appropriate Training',
    tag: 'Vessel-Specific',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Critical cargo securing skills for heavy weather operations and vessel stability. CUSTOMIZE: Add your vessel\'s lashing arrangements and deck cargo limits. (WBC3 Section 28.1.6)'
  },

  {
    id: 'support_vessel_stability',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Load Planning and Vessel Stability Management',
    description: 'Understanding cargo loading effects on vessel stability, trim, and seakeeping',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'support',
    reference: 'Task-Appropriate Training',
    tag: 'Vessel-Specific',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Critical understanding of how cargo loading affects vessel stability and safe operations. CUSTOMIZE: Add your vessel\'s load planning procedures and deck capacity limits. (WBC3 Section 28.1.6)'
  },

  {
    id: 'offshore_positioning_procedures',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Offshore Installation Positioning and Approach',
    description: 'Safe approach and positioning procedures for offshore platforms and structures',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'support',
    reference: 'Task-Appropriate Training',
    tag: 'Vessel-Specific',
    priority: 'high',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Safe approach techniques for offshore installations including weather limitations. CUSTOMIZE: Add platform-specific approach procedures from your operational area. (WBC3 Section 28.1.6)'
  },

  // CARGO OPERATIONS
  {
    id: 'cargo_handling_procedures',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Cargo Handling and Securing Procedures',
    description: 'Safe cargo operations including loading, securing, and discharge',
    trigger: (vessel) => vessel.operations && vessel.operations.includes('cargo_operations'),
    reference: 'Task-Appropriate Training',
    tag: 'Operation-Specific',
    priority: 'high',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Safe cargo handling procedures including securing for sea. CUSTOMIZE: Add your vessel\'s cargo handling equipment and securing procedures. (WBC3 Section 28.1.6)'
  },

  // DANGEROUS GOODS
  {
    id: 'dangerous_goods_handling',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Dangerous Goods Handling and Emergency Procedures',
    description: 'Safe handling of dangerous goods and emergency response',
    trigger: (vessel) => vessel.operations && vessel.operations.includes('dangerous_goods'),
    reference: 'Task-Appropriate Training',
    tag: 'Operation-Specific',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Essential dangerous goods safety including emergency response procedures. CUSTOMIZE: Add specific DG classes you carry and emergency response procedures. (WBC3 Section 28.1.6)'
  },

  // PILOT BOAT OPERATIONS
  {
    id: 'pilot_ladder_deployment',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Pilot Ladder Deployment and Safety Systems',
    description: 'Safe deployment, inspection, and supervision of pilot ladders including SOLAS requirements',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'pilot',
    reference: 'Task-Appropriate Training',
    tag: 'Vessel-Specific',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Essential pilot ladder safety including rigging, positioning, and emergency retrieval procedures. CUSTOMIZE: Add your vessel\'s ladder type and port-specific rigging requirements. (WBC3 Section 28.1.6)'
  },

  {
    id: 'high_speed_close_quarters_maneuvering',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'High-Speed Close Quarters Maneuvering',
    description: 'Advanced boat handling for close approach to ships at speed including collision avoidance',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'pilot',
    reference: 'Task-Appropriate Training',
    tag: 'Vessel-Specific',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Critical high-speed maneuvering skills for safe approach to ships and emergency breakaway procedures. CUSTOMIZE: Add your harbor\'s specific approach protocols and weather limits. (WBC3 Section 28.1.6)'
  },

  {
    id: 'pilot_transfer_communications',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Pilot Transfer Communication Protocols',
    description: 'VHF communication procedures with ships during pilot transfer operations',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'pilot',
    reference: 'Task-Appropriate Training',
    tag: 'Vessel-Specific',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Essential ship-to-pilot boat communications including standard vocabulary and emergency procedures. CUSTOMIZE: Add your port authority VHF channels and standard phrases. (WBC3 Section 28.1.6)'
  },

  {
    id: 'pilot_safety_equipment_operation',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Pilot Transfer Safety Equipment Operation',
    description: 'Safe operation of pilot boat specific safety equipment including MOB recovery systems',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'pilot',
    reference: 'Task-Appropriate Training',
    tag: 'Vessel-Specific',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Critical safety equipment operation including man overboard recovery and emergency beacons. CUSTOMIZE: Add your vessel\'s specific MOB equipment and recovery system locations. (WBC3 Section 28.1.6)'
  },

  {
    id: 'pilot_weather_assessment',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Weather Assessment and Transfer Criteria',
    description: 'Assessment of sea conditions and decision-making protocols for safe pilot transfer',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'pilot',
    reference: 'Task-Appropriate Training',
    tag: 'Vessel-Specific',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Critical weather assessment skills including sea state evaluation and transfer decision criteria. CUSTOMIZE: Add your port\'s weather limits and decision-making protocols. (WBC3 Section 28.1.6)'
  },

  {
    id: 'ship_lee_positioning',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Ship Alongside Positioning and Lee Making',
    description: 'Techniques for creating and maintaining safe lee positions alongside ships',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'pilot',
    reference: 'Task-Appropriate Training',
    tag: 'Vessel-Specific',
    priority: 'high',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Essential lee-making techniques and ship wake management for safe pilot transfer operations. CUSTOMIZE: Add specific techniques for common ship types in your port. (WBC3 Section 28.1.6)'
  },

  {
    id: 'emergency_pilot_recovery',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Emergency Pilot Recovery Procedures',
    description: 'Emergency procedures for pilot recovery including medical emergencies and equipment failures',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'pilot',
    reference: 'Task-Appropriate Training',
    tag: 'Vessel-Specific',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Critical emergency response procedures including pilot recovery and medical emergency protocols. CUSTOMIZE: Add your vessel\'s specific recovery equipment locations and hospital contact details. (WBC3 Section 28.1.6)'
  },

  // ========================================
  // PILOT BOAT FAMILIARISATION - WBC3 Chapter 27
  // WBC3 Table 27.2.3: Alternative Requirements for All Pilot Boats
  // ========================================

  {
    id: 'pilot-transom-steps',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Transom Steps/Ladder: Inspection, Deployment, and Safe Use',
    description: 'Crew must be familiar with transom steps/ladder inspection (wear, corrosion), deployment procedures, and safe boarding/disembarkation techniques.',
    trigger: (vessel) => {
      const type = normalizeVesselType(vessel.type);
      return type === 'pilot' ||
             type === 'pilot_boat_dedicated' ||
             type === 'workboat_pilot_endorsed';
    },
    reference: 'WBC3 Section 14.7.1 - Transom Steps and Ladders',
    tag: 'Pilot Boat Operations',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    vesselTypes: ['pilot', 'pilot_boat_dedicated', 'workboat_pilot_endorsed'],
    tooltip: 'WBC3 Chapter 27 Table 27.2.3 requirement. Safe boarding/disembarkation via transom steps. CUSTOMIZE: Add your vessel\'s transom step configuration and inspection checklist. (WBC3 Section 28.1.6)'
  },

  {
    id: 'pilot-buoyant-lifelines',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Buoyant Lifelines (18m x2): Location, Deployment, and Use',
    description: 'Crew must know location of both 18m buoyant lifelines, deployment procedures, and use for pilot recovery in emergency.',
    trigger: (vessel) => {
      const type = normalizeVesselType(vessel.type);
      return type === 'pilot' ||
             type === 'pilot_boat_dedicated' ||
             type === 'workboat_pilot_endorsed';
    },
    reference: 'WBC3 Section 14.7.2 - Buoyant Lifelines',
    tag: 'Pilot Boat Operations',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    vesselTypes: ['pilot', 'pilot_boat_dedicated', 'workboat_pilot_endorsed'],
    tooltip: 'WBC3 Chapter 27 Table 27.2.3 requirement. Two 18m buoyant lifelines for pilot recovery. CUSTOMIZE: Document stowage locations and deployment procedures. (WBC3 Section 28.1.6)'
  },

  {
    id: 'pilot-propeller-guard',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Propeller Guard: Purpose, Limitations, and Safe Maneuvering',
    description: 'Crew must understand propeller guard purpose (pilot protection), operational limitations, and safe maneuvering techniques near vessels.',
    trigger: (vessel) => {
      const type = normalizeVesselType(vessel.type);
      return type === 'pilot' ||
             type === 'pilot_boat_dedicated' ||
             type === 'workboat_pilot_endorsed';
    },
    reference: 'WBC3 Section 14.7.3 - Propeller Guards',
    tag: 'Pilot Boat Operations',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    vesselTypes: ['pilot', 'pilot_boat_dedicated', 'workboat_pilot_endorsed'],
    tooltip: 'WBC3 Chapter 27 Table 27.2.3 requirement. Protection device for pilot safety during transfer. CUSTOMIZE: Document guard type and any operational limitations. (WBC3 Section 28.1.6)'
  },

  {
    id: 'pilot-transfer-visibility',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Transfer Visibility: Visual Checks for Safe Pilot Transfer',
    description: 'Crew must perform visual checks ensuring clear sightlines to pilot ladder, transfer point, and pilot throughout operation.',
    trigger: (vessel) => {
      const type = normalizeVesselType(vessel.type);
      return type === 'pilot' ||
             type === 'pilot_boat_dedicated' ||
             type === 'workboat_pilot_endorsed';
    },
    reference: 'WBC3 Section 10.1.2 - Field of Vision',
    tag: 'Pilot Boat Operations',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    vesselTypes: ['pilot', 'pilot_boat_dedicated', 'workboat_pilot_endorsed'],
    tooltip: 'WBC3 field of vision requirements for safe pilot transfer operations. CUSTOMIZE: Document any visibility limitations and mitigation measures. (WBC3 Section 28.1.6)'
  },

  {
    id: 'pilot-fall-prevention',
    classification: 'VESSEL_SPECIFIC',
    role: 'UNIVERSAL',
    text: 'Fall Prevention Equipment: Personal Safety Gear for Pilot Operations',
    description: 'Crew must be familiar with personal fall prevention equipment, harness systems, and safety protocols during pilot transfer operations.',
    trigger: (vessel) => {
      const type = normalizeVesselType(vessel.type);
      return type === 'pilot' ||
             type === 'pilot_boat_dedicated' ||
             type === 'workboat_pilot_endorsed';
    },
    reference: 'WBC3 Section 22.2.3 - Protection from Falls',
    tag: 'Pilot Boat Operations',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    vesselTypes: ['pilot', 'pilot_boat_dedicated', 'workboat_pilot_endorsed'],
    tooltip: 'WBC3 fall protection requirements for crew during pilot transfer operations. CUSTOMIZE: Add your vessel\'s harness types and attachment points. (WBC3 Section 28.1.6)'
  },

  {
    id: 'pilot-communication-protocols',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Communication Protocols: Pilot-Vessel Coordination Procedures',
    description: 'Crew must know VHF procedures, hand signals, and communication protocols for coordinating with ship\'s crew during pilot transfer.',
    trigger: (vessel) => {
      const type = normalizeVesselType(vessel.type);
      return type === 'pilot' ||
             type === 'pilot_boat_dedicated' ||
             type === 'workboat_pilot_endorsed';
    },
    reference: 'WBC3 Section 10.2 - Communications',
    tag: 'Pilot Boat Operations',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    vesselTypes: ['pilot', 'pilot_boat_dedicated', 'workboat_pilot_endorsed'],
    tooltip: 'WBC3 communication requirements for safe pilot-vessel coordination. CUSTOMIZE: Add your port\'s VHF channels and hand signal protocols. (WBC3 Section 28.1.6)'
  },

  {
    id: 'pilot-searchlight',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Searchlight: Operation for Night/Low Visibility Transfers',
    description: 'Crew must be able to operate searchlight for illuminating pilot ladder, transfer area, and pilot during night/low visibility operations.',
    trigger: (vessel) => {
      const type = normalizeVesselType(vessel.type);
      return type === 'pilot' ||
             type === 'pilot_boat_dedicated' ||
             type === 'workboat_pilot_endorsed';
    },
    reference: 'WBC3 Section 14.12 - Searchlights',
    tag: 'Pilot Boat Operations',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    vesselTypes: ['pilot', 'pilot_boat_dedicated', 'workboat_pilot_endorsed'],
    tooltip: 'WBC3 Chapter 27 Table 27.2.3 requirement. Essential for night pilot transfers. CUSTOMIZE: Document searchlight location and night operation procedures. (WBC3 Section 28.1.6)'
  },

  {
    id: 'pilot-compact-stretcher',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Compact Stretcher: Location, Deployment, and Emergency Use',
    description: 'Crew must know compact stretcher location, rapid deployment procedures, and use for injured pilot recovery.',
    trigger: (vessel) => {
      const type = normalizeVesselType(vessel.type);
      return type === 'pilot' ||
             type === 'pilot_boat_dedicated' ||
             type === 'workboat_pilot_endorsed';
    },
    reference: 'WBC3 Section 23.1 - First Aid Equipment',
    tag: 'Pilot Boat Operations',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    vesselTypes: ['pilot', 'pilot_boat_dedicated', 'workboat_pilot_endorsed'],
    tooltip: 'WBC3 Chapter 27 Table 27.2.3 requirement. Compact stretcher for pilot emergency recovery. CUSTOMIZE: Add stretcher location and deployment procedures. (WBC3 Section 28.1.6)'
  },

  {
    id: 'pilot-immersion-suits',
    classification: 'VESSEL_SPECIFIC',
    role: 'UNIVERSAL',
    text: 'Immersion Suits: All Crew Must Know Location and Donning Procedure',
    description: 'All crew must know immersion suit location, rapid donning procedures (< 2 minutes), and emergency deployment criteria.',
    trigger: (vessel) => {
      const type = normalizeVesselType(vessel.type);
      return type === 'pilot' ||
             type === 'pilot_boat_dedicated' ||
             type === 'workboat_pilot_endorsed';
    },
    reference: 'WBC3 Section 14.5 - Immersion Suits',
    tag: 'Pilot Boat Operations',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    vesselTypes: ['pilot', 'pilot_boat_dedicated', 'workboat_pilot_endorsed'],
    tooltip: 'WBC3 LSA requirement for pilot boats. All crew require immersion suits. CUSTOMIZE: Document suit locations, sizes, and donning drill requirements. (WBC3 Section 28.1.6)'
  },

  {
    id: 'pilot-mob-procedures',
    classification: 'VESSEL_SPECIFIC',
    role: 'UNIVERSAL',
    text: 'Man Overboard (Pilot Recovery): Emergency Procedures and Drills',
    description: 'Crew must be trained in pilot-specific MOB procedures including immediate actions, recovery techniques, and use of pilot recovery equipment.',
    trigger: (vessel) => {
      const type = normalizeVesselType(vessel.type);
      return type === 'pilot' ||
             type === 'pilot_boat_dedicated' ||
             type === 'workboat_pilot_endorsed';
    },
    reference: 'WBC3 Section 16.3 - Emergency Procedures',
    tag: 'Pilot Boat Operations',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    vesselTypes: ['pilot', 'pilot_boat_dedicated', 'workboat_pilot_endorsed'],
    tooltip: 'WBC3 emergency procedure requirement. Pilot-specific MOB recovery drills. CUSTOMIZE: Add your vessel\'s MOB recovery equipment and drill schedule. (WBC3 Section 28.1.6)'
  },

  {
    id: 'pilot-aborted-transfer',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Aborted Transfer Procedures: Abort Criteria and Safe Extraction',
    description: 'Crew must know abort criteria (weather, ship speed, ladder condition), communication procedures, and safe extraction techniques.',
    trigger: (vessel) => {
      const type = normalizeVesselType(vessel.type);
      return type === 'pilot' ||
             type === 'pilot_boat_dedicated' ||
             type === 'workboat_pilot_endorsed';
    },
    reference: 'WBC3 Section 16.3 - Emergency Procedures',
    tag: 'Pilot Boat Operations',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    vesselTypes: ['pilot', 'pilot_boat_dedicated', 'workboat_pilot_endorsed'],
    tooltip: 'WBC3 emergency procedure requirement. Safe abort and extraction protocols. CUSTOMIZE: Document specific abort criteria and communication signals. (WBC3 Section 28.1.6)'
  },

  {
    id: 'pilot-fast-maneuvering',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Fast Maneuvering Awareness: High-Speed Operations and Station Keeping',
    description: 'Crew must be familiar with vessel\'s fast maneuvering capabilities, station-keeping techniques alongside moving vessels, and high-speed safety procedures.',
    trigger: (vessel) => {
      const type = normalizeVesselType(vessel.type);
      return type === 'pilot' ||
             type === 'pilot_boat_dedicated' ||
             type === 'workboat_pilot_endorsed';
    },
    reference: 'WBC3 Section 10.3 - Maneuverability',
    tag: 'Pilot Boat Operations',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    vesselTypes: ['pilot', 'pilot_boat_dedicated', 'workboat_pilot_endorsed'],
    tooltip: 'WBC3 maneuverability requirement for pilot boat station-keeping alongside ships. CUSTOMIZE: Add your vessel\'s specific maneuvering characteristics and limits. (WBC3 Section 28.1.6)'
  },

  // End of WBC3 Chapter 27 Pilot Boat Items

  // PATROL OPERATIONS - WBC3 Annex 3 Requirements
  {
    id: 'patrol_boarding_procedures',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Vessel Boarding and Search Procedures',
    description: 'Safe boarding techniques, search protocols, and law enforcement procedures including arrest authority',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'patrol',
    reference: 'WBC3 Annex 3 — Police Boat Safety / Maritime Enforcement Powers Code',
    tag: 'Vessel-Specific',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Critical law enforcement procedures including safe boarding, search protocols, and arrest procedures per UK Maritime Enforcement Code. CUSTOMIZE: Add your force\'s specific boarding procedures and operational protocols. (WBC3 Section 28.1.6)'
  },

  {
    id: 'patrol_surveillance_systems',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Night Vision and Surveillance Equipment Operation',
    description: 'Safe operation of thermal imaging, night vision, and surveillance systems including maintenance and emergency procedures',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'patrol',
    reference: 'WBC3 Annex 3 — Police Boat Safety / Enhanced Equipment Requirements',
    tag: 'Equipment-Specific',
    priority: 'critical',
    verifiable: true,
    canBeNA: true,
    wbc3Required: true,
    naReasons: ['No thermal imaging fitted', 'Daylight operations only', 'Basic navigation radar only'],
    tooltip: 'Essential training for night vision and surveillance equipment operation including thermal imaging systems and low-light cameras. CUSTOMIZE: Add your vessel\'s specific surveillance equipment models and procedures. (WBC3 Section 28.1.6)'
  },

  {
    id: 'patrol_high_speed_maneuvering',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'High-Speed Pursuit and Close Quarters Maneuvering',
    description: 'Advanced boat handling for high-speed operations including pursuit procedures and collision avoidance',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'patrol',
    reference: 'WBC3 Annex 3 — Police Boat Safety / High-Risk Operations',
    tag: 'Vessel-Specific',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Critical high-speed maneuvering skills for patrol operations including pursuit tactics and emergency breakaway procedures. CUSTOMIZE: Add your force\'s pursuit protocols and weather limits. (WBC3 Section 28.1.6)'
  },

  {
    id: 'patrol_communications_protocols',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Multi-Agency Communication and Coordination Protocols',
    description: 'VHF and radio procedures for coordinating with Coastguard, Marine Police, and other agencies during operations',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'patrol',
    reference: 'WBC3 Annex 3 — Police Boat Safety / Communication Requirements',
    tag: 'Vessel-Specific',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Essential multi-agency communication protocols including emergency coordination and jurisdictional procedures. CUSTOMIZE: Add your area\'s VHF channels and agency contact procedures. (WBC3 Section 28.1.6)'
  },

  {
    id: 'patrol_evidence_handling',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Evidence Handling and Chain of Custody Procedures',
    description: 'Proper handling, documentation, and preservation of evidence during maritime law enforcement operations',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'patrol',
    reference: 'WBC3 Annex 3 — Police Boat Safety / Law Enforcement Procedures',
    tag: 'Vessel-Specific',
    priority: 'high',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Critical evidence handling procedures including documentation, photography, and chain of custody for maritime arrests. CUSTOMIZE: Add your force\'s evidence procedures and documentation requirements. (WBC3 Section 28.1.6)'
  },

  {
    id: 'patrol_enhanced_safety_equipment',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Enhanced Life-Saving and Safety Equipment Operation',
    description: 'Operation of patrol boat specific safety equipment including enhanced MOB recovery systems and medical equipment',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'patrol',
    reference: 'WBC3 Annex 3 — Enhanced Equipment Requirements',
    tag: 'Equipment-Specific',
    priority: 'high',
    verifiable: true,
    canBeNA: true,
    wbc3Required: true,
    naReasons: ['Standard safety equipment only', 'No enhanced MOB systems', 'Basic first aid equipment only'],
    tooltip: 'Enhanced safety equipment training including specialized MOB recovery systems and advanced medical equipment. CUSTOMIZE: Add your vessel\'s specific enhanced safety equipment locations. (WBC3 Section 28.1.6)'
  },

  // SURVEY OPERATIONS
  {
    id: 'scientific_winch_operation',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Scientific Winch Operation and Load Monitoring',
    description: 'Safe operation of survey winches including load monitoring and emergency procedures',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'survey' || (vessel.equipment && vessel.equipment.includes('scientific_winch')),
    reference: 'Task-Appropriate Training',
    tag: 'Equipment-Specific',
    priority: 'critical',
    verifiable: true,
    canBeNA: true,
    wbc3Required: true,
    naReasons: ['No scientific winch fitted', 'Manual deployment only'],
    tooltip: 'Critical winch safety including load limits, emergency stops, and wire failure procedures. CUSTOMIZE: Add your vessel\'s winch SWL ratings and operational procedures. (WBC3 Section 28.1.6)'
  },

  {
    id: 'overside_equipment_deployment',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Over-Side Equipment Deployment and Recovery',
    description: 'Safe deployment and recovery of survey equipment including snap-back zone awareness',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'survey',
    reference: 'Task-Appropriate Training',
    tag: 'Vessel-Specific',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Essential safety procedures for deploying survey equipment over-side including exclusion zones. CUSTOMIZE: Document your vessel\'s specific equipment and snap-back zones. (WBC3 Section 28.1.6)'
  },

  {
    id: 'survey_station_keeping',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Station Keeping and Drift Management',
    description: 'Maintaining vessel position during equipment deployment using DP or manual positioning',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'survey',
    reference: 'Task-Appropriate Training',
    tag: 'Vessel-Specific',
    priority: 'high',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Critical positioning skills for safe survey operations including environmental awareness. CUSTOMIZE: Add your vessel\'s positioning system (DP/manual) and limitations. (WBC3 Section 28.1.6)'
  },

  {
    id: 'aframe_deck_handling',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'A-Frame and Deck Handling Systems',
    description: 'Safe operation of A-frames, gantries, and deck handling systems including hydraulics',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'survey' && vessel.equipment && vessel.equipment.includes('aframe_gantry'),
    reference: 'Task-Appropriate Training',
    tag: 'Equipment-Specific',
    priority: 'high',
    verifiable: true,
    canBeNA: true,
    wbc3Required: true,
    naReasons: ['No A-frame fitted', 'Manual handling only', 'Fixed deck crane only'],
    tooltip: 'A-frame operation including hydraulic systems, load limits, and emergency procedures. CUSTOMIZE: Add your vessel\'s A-frame SWL and specific hydraulic controls. (WBC3 Section 28.1.6)'
  },

  {
    id: 'survey_equipment_securing',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Survey Equipment Securing for Sea',
    description: 'Proper securing of scientific equipment and deck gear for sea passage',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'survey',
    reference: 'Task-Appropriate Training',
    tag: 'Vessel-Specific',
    priority: 'high',
    verifiable: true,
    canBeNA: false,
    wbc3Required: false,
    tooltip: 'Best practice for securing survey equipment including heavy weather preparations. CUSTOMIZE: Add your vessel\'s specific securing points and lashing requirements. (WBC3 Section 28.1.6)'
  },

  {
    id: 'moonpool_sensor_deployment',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Moonpool and Sensor Deployment Systems',
    description: 'Safe operation of moonpool systems and through-hull sensor deployments',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'survey' && vessel.equipment && vessel.equipment.includes('moonpool'),
    reference: 'Task-Appropriate Training',
    tag: 'Equipment-Specific',
    priority: 'medium',
    verifiable: true,
    canBeNA: true,
    wbc3Required: false,
    naReasons: ['No moonpool fitted', 'Hull-mounted sensors only', 'Over-side deployment only'],
    tooltip: 'Moonpool safety including emergency closure and flooding prevention procedures. CUSTOMIZE: Add your vessel\'s moonpool dimensions and emergency closure procedures. (WBC3 Section 28.1.6)'
  },

  // DIVE SUPPORT OPERATIONS
  {
    id: 'dive_support_procedures',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Dive Support Safety Procedures',
    description: 'Safety procedures for supporting diving operations including surface support protocols',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'dive_support',
    reference: 'Task-Appropriate Training',
    tag: 'Vessel-Specific',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Critical dive support safety including surface protocols and emergency procedures. CUSTOMIZE: Add your vessel\'s dive support procedures and emergency contacts. (WBC3 Section 28.1.6)'
  },

  {
    id: 'dive_ladder_deployment',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Dive Ladder and Platform Deployment',
    description: 'Safe deployment and operation of dive ladders and diving platforms',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'dive_support',
    reference: 'Task-Appropriate Training',
    tag: 'Vessel-Specific',
    priority: 'high',
    verifiable: true,
    canBeNA: true,
    wbc3Required: true,
    naReasons: ['Fixed ladder only', 'No dedicated dive platform'],
    tooltip: 'Safe dive ladder operation including deployment and diver recovery procedures. CUSTOMIZE: Document your vessel\'s ladder type and deployment procedures. (WBC3 Section 28.1.6)'
  },

  {
    id: 'dive_emergency_procedures',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Dive Emergency and Diver Recovery Procedures',
    description: 'Emergency procedures for dive accidents and emergency diver recovery',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'dive_support',
    reference: 'Task-Appropriate Training',
    tag: 'Vessel-Specific',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Critical dive emergency response including diver recovery and medical emergency procedures. CUSTOMIZE: Add nearest recompression chamber location and emergency procedures. (WBC3 Section 28.1.6)'
  },

  // UTILITY VESSEL OPERATIONS  
  {
    id: 'utility_equipment_operation',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Utility Equipment Operation and Safety',
    description: 'Safe operation of utility vessel equipment and multi-purpose systems',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'utility',
    reference: 'Task-Appropriate Training',
    tag: 'Vessel-Specific',
    priority: 'medium',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'General utility vessel equipment operation and safety procedures. CUSTOMIZE: Add your vessel\'s specific utility equipment and operational procedures. (WBC3 Section 28.1.6)'
  },

  {
    id: 'multipurpose_operations',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Multi-Purpose Operations and Equipment',
    description: 'Familiarisation with various operational modes and equipment configurations',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'utility',
    reference: 'Task-Appropriate Training',
    tag: 'Vessel-Specific',
    priority: 'medium',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: 'Understanding different operational configurations for utility vessels. CUSTOMIZE: Document all operational modes your vessel is configured for. (WBC3 Section 28.1.6)'
  },

  // FISHING SUPPORT OPERATIONS
  {
    id: 'fishing_support_equipment',
    classification: 'VESSEL_SPECIFIC',
    role: 'DECK',
    text: 'Fishing Support Equipment and Safety',
    description: 'Safe operation of equipment supporting fishing operations (NOT active fishing)',
    trigger: (vessel) => normalizeVesselType(vessel.type) === 'fishing_support',
    reference: 'Task-Appropriate Training',
    tag: 'Vessel-Specific',
    priority: 'medium',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    tooltip: '⚠️ For support-only vessels — active fishing vessels fall outside the Workboat Code and are not supported. Equipment safety for fishing industry support operations.'
  }
];

/**
 * Generate Task-Specific Familiarisation checklist
 * WBC3-compliant task-based familiarisation per Appendix 8, Section 7.1
 * 
 * Only includes items relevant to actual vessel equipment and operations
 */
export function generateTaskSpecificFamiliarisationChecklist(vessel, crew) {
  const items = [];
  
  TASK_SPECIFIC_FAMILIARISATION_ITEMS.forEach(item => {
    // Check trigger condition
    let includeItem = false;
    
    if (typeof item.trigger === 'function') {
      includeItem = item.trigger(vessel);
    }
    
    if (includeItem) {
      // Add rank visibility based on equipment complexity
      const processedItem = {
        ...item,
        rankVisibility: {
          'Master': 'full_operational',
          'Engineer': 'full_operational',
          'Crew': 'full_operational',
          'Crew Member': 'full_operational',
          'Supernumerary': 'general_awareness_supervised'
        }
      };
      
      items.push(processedItem);
    }
  });
  
  return {
    section: 'task_specific_familiarisation',
    title: 'Task-Specific Familiarisation',
    items: items,
    completionRequired: items.length > 0,
    auditTrail: true,
    description: 'Equipment and operation-specific training per WBC3 Appendix 8, Section 7.1 - training appropriate to tasks undertaken'
  };
}